import { useEffect, useRef, useState } from "react";

/**
 * useState + localStorage — the value hydrates from storage on mount and
 * writes back on every change. Falls back to `initialValue` when storage
 * is empty, throws (SSR, private mode), or holds malformed JSON.
 *
 * Used by dashboards to remember filters/period selections across F5,
 * tab switches and browser restarts. Namespace the key per page (e.g.
 * `scorecard:filters`, `leads:filters`) to avoid collisions.
 *
 * The `version` param invalidates old cached shapes when the initial
 * value structure changes — bump it in code to force everyone to
 * re-hydrate with the new default.
 */
export function usePersistentState(key, initialValue, { version = 1 } = {}) {
  const storageKey = `${key}::v${version}`;
  const hydrated = useRef(false);

  const [value, setValue] = useState(() => {
    if (typeof window === "undefined") return initialValue;
    try {
      const raw = window.localStorage.getItem(storageKey);
      if (raw == null) return initialValue;
      const parsed = JSON.parse(raw);
      // Shallow merge with initial so newly-added keys don't come back
      // as undefined for existing users.
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)
          && initialValue && typeof initialValue === "object" && !Array.isArray(initialValue)) {
        return { ...initialValue, ...parsed };
      }
      return parsed;
    } catch {
      return initialValue;
    }
  });

  // Persist on change. Skip the very first render (already hydrated
  // from storage) to avoid a useless write.
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!hydrated.current) { hydrated.current = true; return; }
    try {
      window.localStorage.setItem(storageKey, JSON.stringify(value));
    } catch {
      // quota exceeded / private mode — swallow silently, feature is
      // best-effort.
    }
  }, [storageKey, value]);

  const clear = () => {
    try { window.localStorage.removeItem(storageKey); } catch { /* noop */ }
    setValue(initialValue);
  };

  return [value, setValue, clear];
}
